<template>
  <div>
    <v-container>
    
      <v-row>
        <v-col cols="12">
          <v-card class="mb-4">
            <v-card-title>
              <h2>{{ listName }}</h2>
            </v-card-title>
        
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="loading">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <p class="mt-3">Lade Sammelliste...</p>
        </v-col>
      </v-row>

   
      <v-row v-if="error">
        <v-col cols="12">
          <v-alert type="error">
            {{ error }}
          </v-alert>
        </v-col>
      </v-row>

      
      <v-row v-if="!loading && !error">
        <v-col cols="12">
          <v-card>
            <v-card-title>
              Sammelliste Status
              <v-spacer></v-spacer>
              <span class="text-h6">
                SOLL: <strong>{{ totalSoll }}</strong> | IST: <strong>{{ totalIst }}</strong>
              </span>
            </v-card-title>
            
          
            <v-card-text>
              <v-progress-linear
                :value="progressPercent"
                height="25"
                color="success"
                striped
              >
                <strong>{{ Math.round(progressPercent) }}%</strong>
              </v-progress-linear>
            </v-card-text>

            <v-data-table
              :headers="headers"
              :items="dataItems"
              :items-per-page="50"
              class="elevation-1"
              no-data-text="Keine Einträge gefunden"
            >
              <template v-slot:item="{ item }">
                <tr :style="item.backgroundColor">
                  <td class="text-center">{{ item.name }}</td>
                  <td class="text-center">{{ item.qtySoll }}</td>
                  <td class="text-center">{{ item.qtyIstBefore }}</td>
                  <td class="text-center">{{ item.qtyIst }}</td>
                 
                  <td class="text-center">{{ item.date }}</td>
                  
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      
      <v-row v-if="!loading && !error">
        <v-col cols="12" class="text-center">
          <v-chip small color="info">
            <v-icon left small>mdi-refresh</v-icon>
            Automatische Aktualisierung alle 30 Sekunden
          </v-chip>
          <p class="mt-2 text-caption">
            Letzte Aktualisierung: {{ lastUpdate }}
          </p>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "PublicCollectingView",
  
  data() {
    return {
      loading: true,
      error: null,
      listName: '',
      listDate: '',
      listPriority: '',
      listCategory: '',
      listNote: '',
      dataItems: [],
      totalSoll: 0,
      totalIst: 0,
      progressPercent: 0,
      lastUpdate: '',
      refreshInterval: null,
      
      headers: [
        { text: 'NAME', value: 'name', align: 'center', sortable: true },
        { text: 'SOLL', value: 'qtySoll', align: 'center', sortable: true },
        { text: 'IST VORHER', value: 'qtyIstBefore', align: 'center', sortable: true },
        { text: 'IST', value: 'qtyIst', align: 'center', sortable: true },
       // { text: 'MITARBEITER', value: 'workerEdit', align: 'center', sortable: true },
        { text: 'LETZTE ÄNDER.', value: 'date', align: 'center', sortable: true },
        
      ]
    }
  },

  props: {
    shareKey: {
      type: String,
      required: true
    }
  },

  async created() {
    await this.loadData();
    this.setupAutoRefresh();
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },

  methods: {
    async loadData() {
      try {
        this.loading = true;
        this.error = null;

        // First, get the collecting list by share_key
        let param = {
          table: "collecting",
          statement: "select",
          type: "detail",
          where: "true",
          selector: "share_key",
          value: this.shareKey,
          short: "false",
          extension: "",
        };

        let data = {};
        let result = await this.$func.queryAPI(param, data);

        if (!result || result.length === 0) {
          this.error = "Sammelliste nicht gefunden oder Link ist ungültig.";
          return;
        }

        const collectingData = result[0];
        const collectingId = collectingData.id;

        // Set list information
        this.listName = collectingData.name;
        this.listDate = collectingData.date;
        this.listPriority = collectingData.priority;
        this.listCategory = collectingData.sort_category;
        this.listNote = collectingData.note;

        // Now get the collecting items
        param = {
          table: "collecting_items",
          statement: "select",
          type: "detail",
          where: "true",
          selector: "collecting_id",
          value: collectingId,
          short: "false",
          extension: "ORDER BY name ASC",
        };

        result = await this.$func.queryAPI(param, data);

        // Process the items
        this.dataItems = [];
        let totalSoll = 0;
        let totalIst = 0;

        for (let i = 0; i < result.length; i++) {
          const item = result[i];
          
          // Determine background color based on completion
          const backgroundColor = Number(item.qty_ist) >= Number(item.qty_soll) 
            ? "background-color: #c8e6c9;" // Light green
            : "background-color: #ffffff;"; // White

          totalSoll += Number(item.qty_soll);
          totalIst += Number(item.qty_ist);

          this.dataItems.push({
            id: item.id,
            name: item.name,
            qtySoll: item.qty_soll,
            qtyIst: item.qty_ist,
            qtyIstBefore: item.qty_ist_before,
            date: this.$moment(item.date_timestamp).format("DD.MM.YYYY"),
            workerEdit: item.worker_edit,
            note: item.note,
            backgroundColor: backgroundColor
          });
        }

        // Sort items (same logic as your original component)
        this.dataItems.sort((a, b) => {
          const nameA = a.name;
          const nameB = b.name;

          const numA = nameA.includes('-') ? nameA.split('-').map(Number) : [parseInt(nameA, 10)];
          const numB = nameB.includes('-') ? nameB.split('-').map(Number) : [parseInt(nameB, 10)];

          const maxLength = Math.max(numA.length, numB.length);

          for (let i = maxLength - 1; i >= 0; i--) {
            const valA = numA[i] || 0;
            const valB = numB[i] || 0;

            if (valA !== valB) {
              return valA - valB;
            }
          }

          return 0;
        });

        this.totalSoll = totalSoll;
        this.totalIst = totalIst;
        this.progressPercent = totalSoll > 0 ? (totalIst * 100) / totalSoll : 0;
        this.lastUpdate = this.$moment().format("DD.MM.YYYY HH:mm:ss");

      } catch (error) {
        console.error("Error loading data:", error);
        this.error = "Fehler beim Laden der Sammelliste. Bitte versuchen Sie es später erneut.";
      } finally {
        this.loading = false;
      }
    },

    setupAutoRefresh() {
      // Refresh every 30 seconds
      this.refreshInterval = setInterval(() => {
        this.loadData();
      }, 30000);
    }
  }
}
</script>

<style scoped>
.v-data-table >>> tbody tr:nth-child(even) {
  background-color: #f5f5f5;
}

.v-data-table >>> tbody tr:hover {
  background-color: #e3f2fd !important;
}
</style>